import { useRef } from "react";
import { isCancel, isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { Label, Switch } from "@libs/zapi-ui/shadcn";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { toast } from "sonner";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { updateFeaturePermission } from "./updateFeaturePermission";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { useTranslation } from "react-i18next";

type TSmallModeSwitchProps = { id?: string; className?: string };

export function SmallModeSwitch(props: TSmallModeSwitchProps) {
  const { id = "labs-mode", className = "" } = props;
  const { t } = useTranslation("common");
  const { isLabs, setMe, role } = useMeStore();
  const { closeHandler, isLabsTabOpen } = useProjectSidebarStore();
  const labsModeAvailable = useFeatureFlag("labs-mode");
  const isAdmin = isSystemAdmin(role);
  const abortControllerRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  if (!labsModeAvailable && !isAdmin) return null;

  const handleChange = async (checked: boolean) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setMe({ isLabs: checked });

    if (!checked && isLabsTabOpen()) {
      closeHandler();
    }

    try {
      await updateFeaturePermission(checked ? 1 : 0, abortControllerRef.current.signal);
    } catch (error) {
      if (isCancel(error)) return;
      if (isAxiosError(error) && error.response?.status === 403) {
        queryClient.invalidateQueries({ queryKey: ["organization-feature-flags"] });
        setMe({ isLabs: false });
        toast.error("Labs mode has been disabled for current organization");
        return;
      }
      console.error(error);
      toast.error(checked ? t("labsMode.enableFailed") : t("labsMode.disableFailed"));
      setMe({ isLabs: !checked });
    }
  };

  return (
    <div data-testid="labs-mode-container" className={`flex flex-col ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <Label htmlFor={id}>
          <FigmaTypography variant="label-2">{t("labsMode.title")}</FigmaTypography>
        </Label>

        <Switch id={id} data-testid="labs-mode-switch" checked={!!isLabs} onCheckedChange={handleChange} />
      </div>
      <FigmaTypography variant="body-3" color="zinc600">
        {t("labsMode.menuDescription")}
      </FigmaTypography>
    </div>
  );
}
